import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useCreateVenue } from "@/hooks/use-venues";
import { useToast } from "@/hooks/use-toast";
import { Plus, MapPin } from "lucide-react";
import { z } from "zod";

export function CreateVenueDialog() {
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState<{
    name: string;
    type: "bar" | "pub" | "club" | "hotel";
    description: string;
    latitude: string;
    longitude: string;
    capacityLevel: string;
  }>({
    name: "",
    type: "bar",
    description: "",
    latitude: "51.505",
    longitude: "-0.09",
    capacityLevel: "0"
  });

  const createVenue = useCreateVenue();
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      await createVenue.mutateAsync({
        name: formData.name,
        type: formData.type,
        description: formData.description,
        latitude: parseFloat(formData.latitude),
        longitude: parseFloat(formData.longitude),
        capacityLevel: parseInt(formData.capacityLevel),
      });

      toast({
        title: "Venue Created",
        description: `${formData.name} has been added to the map.`,
      });
      setOpen(false);
      setFormData({ name: "", type: "bar", description: "", latitude: "51.505", longitude: "-0.09", capacityLevel: "0" });
    } catch (error: any) {
      const message = error?.message || "Failed to create venue. Please check coordinates.";
      toast({
        title: "Error",
        description: message,
        variant: "destructive",
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="button-add-venue" className="rounded-full shadow-lg bg-zinc-800 hover:bg-zinc-700 text-white border border-zinc-700">
          <Plus className="w-4 h-4 mr-2" /> Add Venue
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px] bg-background border-zinc-800 text-white">
        <DialogHeader>
          <DialogTitle className="text-2xl font-display">Add New Spot</DialogTitle>
          <DialogDescription className="text-zinc-400">
            Add a venue — it must exist on Google Maps to be added.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="name">Name</Label>
            <Input 
              id="name" 
              value={formData.name} 
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              className="bg-zinc-900 border-zinc-800 focus:border-primary" 
              required
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="type">Type</Label>
            <Select 
              value={formData.type} 
              onValueChange={(val) => setFormData({...formData, type: val as "bar" | "pub" | "club" | "hotel"})}
            >
              <SelectTrigger className="bg-zinc-900 border-zinc-800">
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800 text-white">
                <SelectItem value="bar">Bar</SelectItem>
                <SelectItem value="pub">Pub</SelectItem>
                <SelectItem value="club">Club</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label htmlFor="lat">Latitude</Label>
              <Input 
                id="lat" 
                value={formData.latitude}
                onChange={(e) => setFormData({...formData, latitude: e.target.value})}
                className="bg-zinc-900 border-zinc-800"
                type="number" step="any" required
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="lng">Longitude</Label>
              <Input 
                id="lng" 
                value={formData.longitude}
                onChange={(e) => setFormData({...formData, longitude: e.target.value})}
                className="bg-zinc-900 border-zinc-800"
                type="number" step="any" required
              />
            </div>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="desc">Description</Label>
            <Textarea 
              id="desc" 
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              className="bg-zinc-900 border-zinc-800"
            />
          </div>
          <Button data-testid="button-submit-venue" type="submit" className="w-full bg-primary text-primary-foreground font-bold hover:bg-primary/90 mt-2" disabled={createVenue.isPending}>
            {createVenue.isPending ? "Verifying & Adding..." : "Add Venue"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
